/**
 * Configuration Context
 *
 * Manages state for tenant configuration administration
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import * as configApi from '../api/configuration';
import { notify } from '../services/notificationBus';

const ConfigurationContext = createContext();

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_CONFIG: 'SET_CONFIG',
  SET_VERSIONS: 'SET_VERSIONS',
  SET_AUDIT_LOG: 'SET_AUDIT_LOG',
  SET_ERROR: 'SET_ERROR',
  SET_VALIDATION: 'SET_VALIDATION',
  SET_MODIFIED: 'SET_MODIFIED',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Initial state
const initialState = {
  config: null,
  originalConfig: null,
  versions: [],
  auditLog: [],
  loading: false,
  error: null,
  validation: null,
  isModified: false
};

// Reducer
function configurationReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTIONS.SET_CONFIG:
      return {
        ...state,
        config: action.payload,
        originalConfig: action.payload,
        isModified: false,
        loading: false
      };

    case ACTIONS.SET_VERSIONS:
      return { ...state, versions: action.payload, loading: false };

    case ACTIONS.SET_AUDIT_LOG:
      return { ...state, auditLog: action.payload, loading: false };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ACTIONS.SET_VALIDATION:
      return { ...state, validation: action.payload };

    case ACTIONS.SET_MODIFIED:
      return { ...state, config: action.payload, isModified: true };

    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
}

// Provider component
//
// Operates on a project-scoped configuration via the
// /api/v1/sessions/{sessionId}/configuration/* routes. `sessionId` is
// required — the tenant-wide variant was retired by FU-002A.
export function ConfigurationProvider({ children, sessionId }) {
  const [state, dispatch] = useReducer(configurationReducer, initialState);

  // Fetch active configuration
  const fetchActiveConfiguration = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: ACTIONS.CLEAR_ERROR });

    try {
      const data = await configApi.getSessionActiveConfiguration(sessionId);
      dispatch({ type: ACTIONS.SET_CONFIG, payload: data });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to load configuration';
      dispatch({ type: ACTIONS.SET_ERROR, payload: errorMessage });
    }
  }, [sessionId]);

  // Fetch version history
  const fetchVersions = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      const data = await configApi.getSessionConfigurationHistory(sessionId);
      dispatch({ type: ACTIONS.SET_VERSIONS, payload: data.versions || [] });
    } catch (error) {
      console.error('Failed to load versions:', error);
    }
  }, [sessionId]);

  // Fetch audit log
  const fetchAuditLog = useCallback(async (limit = 50) => {
    try {
      const data = await configApi.getSessionConfigurationAuditLog(sessionId, limit);
      dispatch({ type: ACTIONS.SET_AUDIT_LOG, payload: data.changes || [] });
    } catch (error) {
      console.error('Failed to load audit log:', error);
    }
  }, [sessionId]);

  // Update configuration (in memory)
  const updateConfiguration = useCallback((updatedConfig) => {
    dispatch({ type: ACTIONS.SET_MODIFIED, payload: updatedConfig });
  }, []);

  // Validate configuration
  const validateConfiguration = useCallback(async (config) => {
    try {
      const result = await configApi.validateConfiguration(config);
      dispatch({ type: ACTIONS.SET_VALIDATION, payload: result });
      return result;
    } catch (error) {
      console.error('Validation failed:', error);
      return { valid: false, errors: [{ field: 'general', error: 'Validation failed' }] };
    }
  }, []);

  // Save configuration (create new version)
  const saveConfiguration = useCallback(async (metadata) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: ACTIONS.CLEAR_ERROR });

    try {
      // Validate first
      const validation = await validateConfiguration(state.config.configuration);
      if (!validation.valid) {
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'Configuration has validation errors' });
        return { success: false, validation };
      }

      const data = await configApi.updateSessionConfiguration(
        sessionId,
        state.config.configuration,
        metadata
      );

      notify({
        type: 'success',
        message: `Configuration v${data.version} created successfully!`
      });

      // Refresh data
      await fetchActiveConfiguration();
      await fetchVersions();

      return { success: true, data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save configuration';
      dispatch({ type: ACTIONS.SET_ERROR, payload: errorMessage });
      notify({ type: 'error', message: errorMessage });

      return { success: false, error: errorMessage };
    }
  }, [state.config, validateConfiguration, fetchActiveConfiguration, fetchVersions, sessionId]);

  // Activate version
  const activateVersion = useCallback(async (version) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      await configApi.activateSessionConfiguration(sessionId, version);
      notify({
        type: 'success',
        message: `Configuration v${version} activated!`
      });

      await fetchActiveConfiguration();
      await fetchVersions();

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to activate version';
      dispatch({ type: ACTIONS.SET_ERROR, payload: errorMessage });
      notify({ type: 'error', message: errorMessage });
      return { success: false, error: errorMessage };
    }
  }, [fetchActiveConfiguration, fetchVersions, sessionId]);

  // Delete version
  const deleteVersion = useCallback(async (version) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      await configApi.deleteSessionConfigurationVersion(sessionId, version);
      notify({
        type: 'success',
        message: `Configuration v${version} deleted!`
      });

      await fetchVersions();
      await fetchAuditLog();

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete version';
      dispatch({ type: ACTIONS.SET_ERROR, payload: errorMessage });
      notify({ type: 'error', message: errorMessage });
      return { success: false, error: errorMessage };
    }
  }, [fetchVersions, fetchAuditLog, sessionId]);

  // Reset to original
  const resetToOriginal = useCallback(() => {
    dispatch({ type: ACTIONS.SET_CONFIG, payload: state.originalConfig });
  }, [state.originalConfig]);

  // Load defaults
  const loadDefaults = useCallback(async () => {
    try {
      const data = await configApi.getDefaultConfiguration();
      const defaultConfig = {
        ...state.config,
        configuration: data.configuration
      };
      dispatch({ type: ACTIONS.SET_MODIFIED, payload: defaultConfig });
      return { success: true };
    } catch (error) {
      console.error('Failed to load defaults:', error);
      return { success: false };
    }
  }, [state.config]);

  const value = {
    // State
    ...state,
    sessionId,

    // Actions
    fetchActiveConfiguration,
    fetchVersions,
    fetchAuditLog,
    updateConfiguration,
    validateConfiguration,
    saveConfiguration,
    activateVersion,
    deleteVersion,
    resetToOriginal,
    loadDefaults
  };

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
}

// Custom hook
export function useConfiguration() {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error('useConfiguration must be used within ConfigurationProvider');
  }
  return context;
}
